import { formatFollowersCount } from "@/Helpers/format-helper";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/shadcnComponents/ui/card";
import axios from "axios";
import { Check, PlusIcon } from "lucide-react";
import mixpanel from "mixpanel-browser";
import { useEffect, useRef, useState } from "react";
import { FaLinkedin } from "react-icons/fa";
import { twMerge } from "tailwind-merge";

const host = import.meta.env.VITE_BACKEND_SERVER_URL;

const FollowCreatorList = ({
  handleSubmit,
}: {
  handleSubmit: () => Promise<void>;
}) => {
  // hooks
  const initialized = useRef(false);

  // states
  const [followingCreators, setFollowingCreators] = useState<any[]>([]);
  const [creatorList, setCreatorList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mixpanel.track("Follow Other Creators Page Loaded");

    const fetchCreatorList = async () => {
      try {
        const res = await axios.get(
          `${host}/influencer/getCreatorForFollowing`,
          {
            headers: {
              jwtToken: localStorage.getItem("jwtToken"),
            },
          }
        );
        const { data } = res;

        if (data?.success) {
          const shortedBasedOnNullImage = data?.data.sort((a, b) => {
            if (a.image.length === 0) return 1;
            if (b.image.length === 0) return -1;
            return 0;
          });

          setCreatorList(shortedBasedOnNullImage);

          setFollowingCreators(
            data?.data
              ?.filter((creator) => creator?.isFollowing)
              .map((creator) => creator?.id) || []
          );
        }
      } catch (error) {
        console.log("Unable to fetch creator for following", error);
      } finally {
        setLoading(false);
      }
    };

    if (!initialized.current) {
      initialized.current = true;
      fetchCreatorList();
    }
  }, []);

  const handleFollowing = async (id) => {
    if (followingCreators.includes(id)) {
      setFollowingCreators(
        followingCreators.filter((creatorId) => creatorId !== id)
      );

      try {
        await axios.post(
          `${host}/influencer/unfollowCreator`,
          { creatorId: id },
          {
            headers: {
              jwtToken: localStorage.getItem("jwtToken"),
            },
          }
        );
      } catch (error) {
        console.log("Unable to unfollow creator", error);
      }

      return;
    }
    mixpanel.track("Clicked on Following Button", { followedCeatorId: id });
    setFollowingCreators([...followingCreators, id]);

    try {
      await axios.post(
        `${host}/influencer/followCreator`,
        { creatorId: id, source: "network" },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
    } catch (error) {
      console.log("Unable to follow creator", error);
    }
  };

  return (
    <>
      <Card className="w-full font-inter bg-[#171717] border-[#262626] text-[#FFFFFF] rounded-[8px] gap-[20px] ">
        <CardHeader className=" gap-[0px] "></CardHeader>
        <CardContent className="p-0 md:p-6 pt-0 mb-16 ">
          {loading && (
            <div className="animate-pulse grid grid-cols-2 gap-2 md:flex md:gap-5 md:flex-wrap">
              {Array.from({ length: 10 }).map((_, index) => {
                return (
                  <div
                    key={index}
                    className="w-[150px] h-[200px] md:w-[180px] md:h-[250px] bg-[#262626] rounded-[10px]"
                  />
                );
              })}
            </div>
          )}
          {!loading && (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-1 md:gap-4">
              {creatorList.map((creator, index) => (
                <FollowCreatorCard
                  creatorImg={creator?.image}
                  name={creator?.name}
                  slug={creator?.slug}
                  key={index}
                  followers={creator?.followers}
                  isFollowing={followingCreators.includes(creator?.id)}
                  onFollow={() => handleFollowing(creator?.id)}
                  isFeatured={creator?.isFeatured}
                />
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="flex text-[12px] md:text-[14px] mt-[10px] bg-[#DC2627] hover:bg-[#dc2644]  md:w-auto"
            >
              Save & Continue
            </Button>
          </div>
        </CardContent>
        <CardFooter></CardFooter>
      </Card>
    </>
  );
};

type FollowCreatorCardProps = {
  creatorImg: string;
  name: string;
  slug: string;
  followers: string;
  isFollowing?: boolean;
  onFollow?: () => void;
  isFeatured?: boolean;
};

const FollowCreatorCard = ({
  creatorImg,
  name,
  slug,
  followers,
  isFollowing,
  onFollow,
  isFeatured,
}: FollowCreatorCardProps) => {
  followers = formatFollowersCount(parseInt(followers), { lowercase: true });

  return (
    <section className="w-fit shrink-0  md:flex md:flex-col md:gap-[28px]">
      <div className="p-[10px] rounded-[8px] border-[1px] border-solid border-[#272727] backdrop-blur">
        <div className="relative size-[140px] md:size-[180px]">
          {isFeatured && (
            <div className="flex absolute top-0 left-0 bg-[#272727] justify-center items-center gap-2 p-[6px] rounded-[8px] ml-[3px] mt-[2px] bg-[linear-gradient(92deg,_rgba(241,_241,_241,_0.16)_1.57%,_rgba(243,_171,_171,_0.00)_49.29%,_rgba(241,_241,_241,_0.20)_96.06%)]">
              <div className="size-[12px]">
                <img
                  src={
                    "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Lark20240610-154257.gif"
                  }
                  alt="logo"
                  className="w-full h-full"
                />
              </div>
              <span className="rounded text-white text-center text-[9px] not-italic font-bold leading-[normal] font-[Inter]">
                FEATURED
              </span>
            </div>
          )}
          <img
            src={creatorImg}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null; // prevents looping
              currentTarget.src =
                "https://collab.anchors.in/FeatredCreatorBackground.png";
            }}
            alt={`Pic`}
            className="z-[1] object-cover h-full w-full rounded-[8px]"
          />
          <div
            className={twMerge(
              `z-[2] absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-t from-[#171717] to-transparent`,
              `from-[#101010]`
            )}
          >
            <div className="flex flex-col justify-between w-full h-full">
              <div></div>
              <span
                className="w-full text-center text-white text-[12px] md:text-[16px] not-italic font-bold leading-[normal] font-[Inter] px-[16px] cursor-pointer hover:text-red-400"
                onClick={() => {
                  mixpanel.track("Clicked on creator Check Profile link", {
                    creatorName: name,
                    slug: slug,
                  });
                  window.open(`${window.location.origin}/${slug}`, "_blank");
                }}
              >
                {name}
              </span>
            </div>
          </div>
        </div>

        <div className="px-[16px] flex flex-col gap-[20px] bg-[#101010] rounded-b-[8px]">
          <div className="flex justify-center">
            <span className="text-xs md:text-sm text-slate-500 flex gap-1 items-center mt-1">
              <FaLinkedin color="#71b7fb" />
              {followers}
            </span>
          </div>

          <div className="mb-[16px] flex flex-col items-center gap-4">
            <Button
              className={`group/follow-btn text-[#ffff] font-bold font-[Inter] text-[12px] not-italic leading-[normal] w-full gap-2 bg-transparent border border-[#F1F1F1] hover:bg-[#F1F1F1] hover:text-[#171717] hover:border-[#F1F1F1] py-[8px] px-[16px] rounded-full ${
                isFollowing
                  ? "bg-[#F1F1F1] text-[#171717] border-[#F1F1F1]"
                  : ""
              }`}
              variant="outline"
              size="sm"
              onClick={() => {
                onFollow();
              }}
            >
              {isFollowing ? (
                <>
                  <Check size={16} /> Following
                </>
              ) : (
                <>
                  <PlusIcon size={16} /> Follow
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FollowOtherCreators = ({
  handleSubmit,
}: {
  handleSubmit: () => Promise<void>;
}) => {
  const [currentPage, setCurrentPage] = useState("notfollowing");

  return (
    <>
      <div className="w-full   min-h-screen flex flex-col items-center justify-start bg-[#171717] h-full ">
        <div className=" w-full h-full py-[25px] flex flex-col items-start px-[16px] md:p-[40px] text-[#FFFFFF] gap-[22px]  md:gap-[46px] bg-[#171717]">
          <div className="w-full flex justify-between">
            <div className=" flex flex-col items-start gap-[8px] ">
              <h1 className="text-[14px] md:text-[24px] font-[600] font-inter">
                Get Inspired!
              </h1>
              <span className="text-[12px] md:text-[14px] font-[400] text-[#A3A3A3] font-inter">
                Follow your peers & up your content game
              </span>
            </div>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="flex text-[12px] md:text-[14px] bg-[#DC2627] hover:bg-[#dc2644]  md:w-auto"
            >
              Save & Continue
            </Button>
          </div>

          {currentPage === "notfollowing" && (
            <FollowCreatorList handleSubmit={handleSubmit} />
          )}
        </div>
      </div>
    </>
  );
};
